import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { partnerAPI } from '../services/api';

export default function TenantAccessPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [error, setError] = useState(null);
  const [tenantName, setTenantName] = useState('');

  useEffect(() => {
    assumeTenant();
  }, [tenantId]);

  const assumeTenant = async () => {
    try {
      setStatus('loading');
      setError(null);

      const data = await partnerAPI.assumeTenant(tenantId);
      const { token, tenant } = data;
      const subdomain = tenant.subdomain;

      setTenantName(tenant.name);
      setStatus('success');

      // Store the partner JWT for the target tenant
      // 1. Cookie on the root domain (for cross-subdomain access)
      // Extract the root domain from the current hostname
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // For domains like subdomain.example.com, we want .example.com
      // For localhost or IP addresses, we don't set a domain
      let cookieOptions = {
        secure: true,
        sameSite: 'lax',
        expires: 1, // 1 day (token itself expires in 30min)
      };

      if (parts.length >= 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
        // Not an IP address and has at least 2 parts
        cookieOptions.domain = `.${parts.slice(-2).join('.')}`;
      }

      Cookies.set('elebyte_id_token', token, cookieOptions);

      // 2. localStorage for the target subdomain (Platform reads these)
      // Note: We can't write to another subdomain's localStorage directly,
      // but the cookie will be picked up by the Platform's auth flow.
      // The Platform's authorizer reads cookies as fallback.

      // 3. Redirect to the tenant's Platform app
      setTimeout(() => {
        // Build the redirect URL using the current domain
        const currentHostname = window.location.hostname;
        const domainParts = currentHostname.split('.');

        // Extract the root domain (e.g., elebyte.io from partners.elebyte.io)
        let rootDomain = currentHostname;
        if (domainParts.length >= 2 && !currentHostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
          rootDomain = domainParts.slice(-2).join('.');
        }

        // Construct the tenant URL
        const protocol = window.location.protocol;
        window.location.href = `${protocol}//${subdomain}.${rootDomain}`;
      }, 500);
    } catch (err) {
      setStatus('error');
      setError(
        err.response?.data?.error ||
        err.message ||
        'Failed to access tenant. Please try again.'
      );
    }
  };

  return (
    <Box className="pt-stage">
      <Box className="pt-panel" sx={{ textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress size={40} sx={{ mb: 3 }} />
            <h1 className="pt-title">Connecting</h1>
            <p className="pt-lede">Setting up your session for the tenant.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CircularProgress size={40} sx={{ mb: 3 }} />
            <h1 className="pt-title">Connected</h1>
            <p className="pt-lede">Taking you to {tenantName}…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="pt-title">That didn&apos;t work</h1>
            <Alert severity="error" sx={{ my: 3, textAlign: 'left' }}>{error}</Alert>
            <Button variant="contained" startIcon={<BackIcon />} onClick={() => navigate('/')} fullWidth>
              Return to Dashboard
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
